import { graphqlRequest } from './graphqlClient.js';
import type { ShellHydration } from './hydration.js';

export type AnnouncementEventType = 'shown' | 'seen' | 'clicked' | 'dismissed';

export interface TrackAnnouncementEventInput {
  announcementId: string;
  eventType: AnnouncementEventType;
  sessionId?: string;
  anonymousId?: string;
  projectUserId?: string;
  meta?: Record<string, unknown>;
}

const ANNOUNCEMENT_FIELDS = `
  _id publicId title description bodyHtml bodySummary badge badgeColor publishedAt
  collectionName collectionSlug collectionPublicId collectionType
  projectName projectSlug projectLogoUrl
  layout
  theme { preset colors { background text button badge } fontFamily shape coverType }
  cta { enabled label url }
  coverImage { key url }
`;

const FEED_QUERY = `query GetPublicFeed($input: GetPublicFeedInput!) {
  getPublicFeed(input: $input) {
    announcements { ${ANNOUNCEMENT_FIELDS} }
    total
    title
    subtitle
    logoUrl
  }
}`;

const ANNOUNCEMENT_QUERY = `query GetPublicAnnouncement($input: GetPublicAnnouncementInput!) {
  getPublicAnnouncement(input: $input) { ${ANNOUNCEMENT_FIELDS} }
}`;

const TRACK_EVENT_MUTATION = `mutation TrackAnnouncementEvent($input: TrackAnnouncementEventInput!) {
  trackAnnouncementEvent(input: $input)
}`;

export interface PublicFeedInput {
  publicId?: string;
  projectId?: string;
  clientId?: string;
  collectionSlug?: string;
  limit?: number;
  offset?: number;
  // Identity hints so the authenticated SDK feed can include segment-
  // targeted announcements the user matches. Omitted from the anonymous
  // public /public/<slug>/changelog page caller.
  email?: string;
  uniqueId?: string;
}

export interface PublicFeedResult {
  announcements: unknown[];
  total: number;
  title?: string;
  subtitle?: string;
  logoUrl?: string;
}

export async function fetchPublicFeed(
  hydration: ShellHydration,
  input: PublicFeedInput,
): Promise<PublicFeedResult> {
  const response = await graphqlRequest<{ getPublicFeed: PublicFeedResult | null }>(hydration, {
    query: FEED_QUERY,
    variables: { input },
    operationName: 'GetPublicFeed',
  });
  return response.data?.getPublicFeed ?? { announcements: [], total: 0 };
}

export async function fetchPublicAnnouncement(
  hydration: ShellHydration,
  publicId: string,
): Promise<unknown | null> {
  const response = await graphqlRequest<{ getPublicAnnouncement: unknown }>(hydration, {
    query: ANNOUNCEMENT_QUERY,
    variables: { input: { publicId } },
    operationName: 'GetPublicAnnouncement',
  });
  return response.data?.getPublicAnnouncement ?? null;
}

export async function trackAnnouncementEvent(
  hydration: ShellHydration,
  input: TrackAnnouncementEventInput,
): Promise<void> {
  if (!input.announcementId) return;
  try {
    const response = await graphqlRequest<{ trackAnnouncementEvent: boolean }>(hydration, {
      query: TRACK_EVENT_MUTATION,
      variables: {
        input: {
          projectId: hydration.projectId,
          announcementId: input.announcementId,
          eventType: input.eventType,
          sessionId: input.sessionId,
          anonymousId: input.anonymousId,
          projectUserId: input.projectUserId,
          meta: input.meta,
        },
      },
      operationName: 'TrackAnnouncementEvent',
    });
    // Surface GraphQL errors so missing auth headers don't fail silently.
    if (response.errors?.length) {
      console.warn('[chirp] trackAnnouncementEvent failed:', response.errors[0]?.message);
    }
  } catch (err) {
    console.warn('[chirp] trackAnnouncementEvent network error:', err);
  }
}
